import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

/**
 * Generic overlay dialog. Instantiate it behind an `@if` at the call site (it has no
 * internal open/closed state of its own) — mirrors how KanbanTaskDetailComponent is
 * mounted/unmounted by its parent rather than toggling visibility internally.
 */
@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent implements OnDestroy {
  private static nextId = 0;

  readonly title = input('');
  /** One line under the title; also the dialog's accessible description. */
  readonly subtitle = input('');
  /** `lg` widens the panel on laptop screens for longer forms. */
  readonly size = input<'md' | 'lg'>('md');
  /** Below `sm`, render as a full-screen sheet instead of a floating card (long forms on phones). */
  readonly fullScreenOnMobile = input(false);
  readonly closed = output<void>();

  protected readonly panelClasses = computed(() => {
    const width = this.size() === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-115';
    return this.fullScreenOnMobile()
      ? `h-dvh max-h-dvh rounded-none sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-xl ${width}`
      : `max-h-[calc(100dvh-2rem)] max-w-115 rounded-xl ${width}`;
  });

  private readonly instanceId = ModalComponent.nextId++;
  protected readonly titleId = `app-modal-heading-${this.instanceId}`;
  protected readonly subtitleId = `app-modal-subtitle-${this.instanceId}`;

  private readonly injector = inject(Injector);
  private readonly panelEl = viewChild<ElementRef<HTMLElement>>('panel');

  constructor() {
    document.body.style.overflow = 'hidden';
    afterNextRender(() => this.panelEl()?.nativeElement.focus(), { injector: this.injector });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  protected close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closed.emit();
  }
}
